import React from 'react';
import { 
  Container, 
  Wrapper, 
  BrandingHeader,
  LogoCircle,
  LogoText,
  Title, 
  Desc, 
  MetricsRow,
  MetricItem,
  MetricValue,
  MetricLabel,
  FunnelContainer,
  CenterPiece,
  CenterImage,
  Node,
  IconCircle,
  NodeTitle,
  NodeDesc,
  SVGOverlay,
  AnimatedLine
} from './WorkflowStyle';
import { pmLifecycle } from '../../data/constants';
import { motion } from 'framer-motion';
import { Search, Edit3, Layers, Rocket, RefreshCcw } from 'lucide-react';

const icons = [Search, Edit3, Layers, Rocket, RefreshCcw];

const metrics = [
  { label: "Years Exp.", value: "1+" },
  { label: "Features Shipped", value: "25+" },
  { label: "PRD Docs", value: "20+" },
  { label: "User Satisfaction ▲", value: "35%" },
];

// Positions for the circle layout (relative to center)
// Layout positions for a 360-degree true circular arrangement
const positions = [
  { top: '8%', left: '72%', align: 'flex-start' },    // Top Right
  { top: '48%', left: '76%', align: 'flex-start' },   // Mid Right
  { top: '85%', left: '38%', align: 'center', transform: 'translateX(-50%)' }, // Bottom Center
  { top: '48%', left: '0%', align: 'flex-end' },      // Mid Left
  { top: '8%', left: '2%', align: 'flex-end' },       // Top Left
];

const Workflow = () => {
  // Precision mathematical line anchor points targeting the CenterPiece
  const linePoints = [
    { x1: 70, y1: 15 }, // Top Right
    { x1: 75, y1: 50 }, // Mid Right
    { x1: 50, y1: 82 }, // Bottom
    { x1: 27, y1: 51 }, // Mid Left
    { x1: 28, y1: 15 }, // Top Left
  ];

  return (
    <Container id="workflow">
      <Wrapper>
        <BrandingHeader
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <LogoCircle>
            <LogoText>RK</LogoText>
          </LogoCircle>
          <Title>The PM Lifecycle</Title>
          <Desc>From idea to impact: A disciplined 0→1→N journey turning concepts into scalable, user-loved products.</Desc>
        </BrandingHeader>

        <MetricsRow>
          {metrics.map((m, i) => (
            <MetricItem
              key={i}
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <MetricValue>{m.value}</MetricValue>
              <MetricLabel>{m.label}</MetricLabel>
            </MetricItem>
          ))}
        </MetricsRow>
        
        <FunnelContainer>
          {/* Moving Wires Overlay */}
          <SVGOverlay viewBox="0 0 100 100" preserveAspectRatio="none">
            {linePoints.map((pt, i) => (
              <AnimatedLine 
                key={i}
                d={`M ${pt.x1} ${pt.y1} L 50 50`}
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, delay: 0.5 + i * 0.1 }}
              />
            ))}
          </SVGOverlay>

          {/* Central Anchor */}
          <CenterPiece
            as={motion.div}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <CenterImage src="https://raw.githubusercontent.com/Rakeshere/01portfolio/main/public/HeroImage1.jpg" alt="Whiteboard Strategy" />
          </CenterPiece>

          {/* Process Nodes */}
          {pmLifecycle.map((step, index) => {
            const Icon = icons[index];
            const pos = positions[index];
            return (
              <Node
                key={step.id}
                as={motion.div}
                initial={{ opacity: 0, x: index < 2 ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                style={{ 
                  top: pos.top, 
                  left: pos.left, 
                  alignItems: pos.align,
                  textAlign: pos.align === 'flex-start' ? 'left' : pos.align === 'flex-end' ? 'right' : 'center',
                  transform: pos.transform
                }}
              >
                <IconCircle>
                  <Icon size={24} />
                </IconCircle>
                <NodeTitle>{step.title}</NodeTitle>
                <NodeDesc>{step.desc}</NodeDesc>
              </Node>
            );
          })}
        </FunnelContainer>
      </Wrapper>
    </Container>
  );
};

export default Workflow;

